import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { SpreadsheetGrid } from "@components/spreadsheet/SpreadsheetGrid";
import {
    appendSpreadsheetActionsColumn,
    isSpreadsheetActionsColumn,
} from "@components/spreadsheet/spreadsheetActionsColumn";
import { RemoveSpreadsheetRowButton } from "@components/spreadsheet/RemoveSpreadsheetRowButton";
import { ColumnDef } from "@components/spreadsheet/types";
import { AccrualImporterRow } from "../types";
import drawerStyles from "../AccrualImporterDrawer.module.scss";
import { getLineItemColumns } from "./groupHelpers";
import { useAccrualImporterSubsetChangeHandler } from "./useAccrualImporterSubsetChangeHandler";
import styles from "./AccrualImporterGroupedSections.module.scss";

interface Props {
    lineRows: AccrualImporterRow[];
    globalRowIndices: number[];
    columns: ColumnDef[];
    setRows: React.Dispatch<React.SetStateAction<AccrualImporterRow[]>>;
    addLineItemToGroup: (rowIndex: number) => void;
    removeRow: (rowIndex: number) => void;
    className?: string;
}

export const AccrualImporterLineItemsGrid: React.FC<Props> = ({
    lineRows,
    globalRowIndices,
    columns,
    setRows,
    addLineItemToGroup,
    removeRow,
    className,
}) => {
    const lineItemColumns = useMemo(
        () => getLineItemColumns(columns),
        [columns],
    );

    const gridColumns = useMemo(
        () =>
            appendSpreadsheetActionsColumn(lineItemColumns, {
                label: "",
                width: 140,
                cellFn: ({ rowIdx }) => {
                    if (rowIdx >= lineRows.length) {
                        return null;
                    }

                    const globalRowIndex = globalRowIndices[rowIdx];
                    if (globalRowIndex === undefined) {
                        return null;
                    }

                    return (
                        <div className={drawerStyles.actionsCell}>
                            <button
                                type="button"
                                className={drawerStyles.addLineButton}
                                onClick={() =>
                                    addLineItemToGroup(globalRowIndex)
                                }
                            >
                                + add line item
                            </button>
                            {lineRows.length > 1 && (
                                <RemoveSpreadsheetRowButton
                                    onClick={() => removeRow(globalRowIndex)}
                                />
                            )}
                        </div>
                    );
                },
            }),
        [
            addLineItemToGroup,
            globalRowIndices,
            lineItemColumns,
            lineRows.length,
            removeRow,
        ],
    );

    const handleChange = useAccrualImporterSubsetChangeHandler({
        setRows,
        columns: lineItemColumns,
        globalRowIndices,
    });

    const isCellEditable = useCallback(
        (rowIdx: number, colIdx: number) => {
            const col = gridColumns[colIdx];
            if (!col || col.field === "_rowNumber") {
                return false;
            }
            if (isSpreadsheetActionsColumn(col)) {
                return false;
            }
            if (col.field === "amount") {
                return false;
            }
            return rowIdx < lineRows.length;
        },
        [gridColumns, lineRows.length],
    );

    return (
        <SpreadsheetGrid
            className={classNames(styles.lineGrid, className)}
            data={lineRows}
            columns={gridColumns}
            onChange={handleChange}
            isCellEditable={isCellEditable}
            minSpareRows={0}
            noOuterBorder
            stickyHeader
            showTotalsRow
            overflow="auto"
            lightNotEditableCells
        />
    );
};
